import React from "react";
import { Order, OrderStatus } from "../../../../core/models/entities/Order";
import {
  faBan,
  faCreditCard,
  faEye,
  faNoteSticky,
  faPen,
  faTags,
} from "@fortawesome/free-solid-svg-icons";
import { nameOf } from "../../../../utils/functions/ReflectionUtil";
import { ColumnSettings } from "../../../components/GenericTable/SubComponents/ListFrame";
import IconButton from "../../../components/LogoButton/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDate } from "../../../../utils/functions/DateUtil";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import { OrderStatusElement } from "./OrderStatusElement";
import {
  OrderState,
  getOrderState,
} from "../vmHooks/useFunctionalListOrdersViewModel";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useCartHidden } from "../../../../hooks/useCartHidden";
import { useWorkshopMode } from "../../../../hooks/useWorkshopMode";
import {
  ApprovalVoting,
  Verdict,
} from "../../../components/ApprovalVoting/approval-voting";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  openStripePayment,
  orderAwaitsStripePayment,
  useCanAdjustOrderPrices,
  useCanCancelAwaitingOrders,
  useCanPayOrders,
} from "../../../components/Order/PayNow";
import { orderTrackingUrl } from "../../../components/Order/tracking";
import { requestedDeliveryToShow } from "../../order-details/requestedDelivery";
import { enviroment } from "../../../../enviroment";
import { PriceAdjustedBadge } from "./PriceAdjustedBadge";
import { orderRowKeyDown } from "../functions/orderRowClick";
import {
  CLARITY_MASK,
  CLARITY_MASK_TOOLTIP,
  CLARITY_UNMASK,
} from "../../../../utils/analytics/clarityAttrs";

interface OrderRowItem {
  order: Order;
  columns: ColumnSettings;
  onEditDesign: (order: Order) => void;
  onPlaceVerdict: (order: Order, verdict: Verdict) => void;
  onSeeRejectNote: (order: Order) => void;
  onCancelOrder?: (order: Order) => void;
  // Admin-only price adjustment of Awaiting payment rows (flag-gated in the
  // hook); undefined = no button rendered.
  onAdjustPrices?: (order: Order) => void;
  isLoading?: boolean;
}

export function OrderRowItem({
  order,
  columns,
  onSeeRejectNote,
  onPlaceVerdict,
  onEditDesign,
  onCancelOrder,
  onAdjustPrices,
  isLoading,
}: OrderRowItem) {
  const { t } = useTranslation();
  const requestedDelivery = requestedDeliveryToShow(
    order,
    enviroment.requestedDeliveryEnabled
  );
  const navigate = useNavigate();
  const { profile } = useProfileContext();
  // Hidden-price customers keep the order list (status/dates carry no prices),
  // but Edit/View lead to screens with line prices and totals - hide them, same
  // convention as the cart icon.
  const cartHidden = useCartHidden();
  const workshopMode = useWorkshopMode();
  // Only Vendor/Admin may pay — an end customer never sees "Pay now" (for an
  // end-customer order the vendor pays). Same rule as every other Pay now surface.
  const canPay = useCanPayOrders();
  // Admin-only cancel of parked orders (flag-gated in the hook).
  const canCancel = useCanCancelAwaitingOrders();
  // Admin-only "Adjust prices" on parked orders (flag-gated in the hook).
  const canAdjustPrices = useCanAdjustOrderPrices();
  const isNotCustomer =
    order.organization.id !==
    profile?.userOrganizationInformations.organizationId;

  function EditOrder(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onEditDesign(order);
  }

  function SeeRejectNote(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onSeeRejectNote(order);
  }

  function ViewOrder(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    navigate(
      `/order/details?orderId=${order.id}&orgId=${order.organization.id}`,
    );
  }

  function PayNow(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    openStripePayment(order.stripePaymentUrl!);
  }

  function CancelOrder(e: React.MouseEvent<HTMLElement>) {
    // The whole row navigates on click - never let the cancel click through.
    e.stopPropagation();
    onCancelOrder?.(order);
  }

  function AdjustPrices(e: React.MouseEvent<HTMLElement>) {
    e.stopPropagation();
    onAdjustPrices?.(order);
  }

  // Dead clicks round 2 (flag clarityUx2Enabled): the whole row already
  // opens the order (ListItemFrame's click) but says so nowhere - Clarity
  // saw clicks on the row text counted as dead. With the flag on the row
  // carries "Open order" as its title (hover hint, and what a screen reader
  // announces for the focused row), is focusable and opens on Enter/Space;
  // only where a click really opens it (a hidden-price customer gets the
  // toast from the screen instead). Deliberately NO role="link"/aria-label:
  // the row holds its own buttons (View, Pay now, ...) and interactive
  // content inside a link is invalid ARIA (skeptic 26/9). Off = today's
  // <div>, byte for byte.
  const rowOpens = !cartHidden || workshopMode;
  const ux2RowProps =
    enviroment.clarityUx2Enabled && rowOpens
      ? {
          title: t("ux2.openOrder"),
          tabIndex: 0,
          onKeyDown: orderRowKeyDown,
        }
      : {};

  const overallState = getOrderState(order);
  const actions = (
    <>
      {overallState === OrderState.Rejected && (
        <>
          <IconButton
            key={"see-note"}
            onClick={SeeRejectNote}
            icon={<FontAwesomeIcon icon={faNoteSticky} />}
          >
            {t("orderListDetails.seeNote")}
          </IconButton>
        </>
      )}
      {overallState === OrderState.Pending && (
        <ApprovalVoting
          message={t("orderListDetails.approveOrderPrompt")}
          placeVerdict={(verdict) => onPlaceVerdict(order, verdict)}
          approvalGroup={order.approval!}
          rejectDelete={() => {}}
          isForOrders={true}
          isLoading={isLoading}
        />
      )}
      {overallState === OrderState.Draft && !cartHidden && (
        <IconButton
          key={"approve"}
          onClick={EditOrder}
          icon={<FontAwesomeIcon icon={faPen} />}
        >
          {t("edit")}
        </IconButton>
      )}
      {/* Stripe prepayment: Vendor/Admin only (end customer never pays), and
          gate on the payment link too - a parked order without a link (session
          creation failed) has nothing to open */}
      {canPay && orderAwaitsStripePayment(order) && (
        <IconButton
          key={"pay-now"}
          onClick={PayNow}
          variant={"success"}
          icon={<FontAwesomeIcon icon={faCreditCard} />}
        >
          {t("orderPayment.payNow")}
        </IconButton>
      )}
      {/* Admin cancel for parked orders. Gated on STATUS alone (not
          orderAwaitsStripePayment): a row whose Checkout session failed has no
          payment link and needs cancelling the most. */}
      {canCancel && order.status === OrderStatus.AwaitingPayment && onCancelOrder && (
        <IconButton
          key={"cancel-order"}
          onClick={CancelOrder}
          variant={"error"}
          icon={<FontAwesomeIcon icon={faBan} />}
        >
          {t("orderPayment.cancelOrder")}
        </IconButton>
      )}
      {/* Admin price adjustment (agreed prices -> new payment link). Gated
          on STATUS alone like cancel: the API decides (409) whether the
          order still qualifies. */}
      {canAdjustPrices &&
        order.status === OrderStatus.AwaitingPayment &&
        onAdjustPrices && (
          <IconButton
            key={"adjust-prices"}
            onClick={AdjustPrices}
            icon={<FontAwesomeIcon icon={faTags} />}
          >
            {t("orderPriceAdjustment.button")}
          </IconButton>
        )}
      {(overallState === OrderState.InProduction ||
        overallState === OrderState.Shipped) &&
        (!cartHidden || workshopMode) && (
          <IconButton
            key={"approve"}
            onClick={ViewOrder}
            icon={<FontAwesomeIcon icon={faEye} />}
          >
            {t("view")}
          </IconButton>
        )}
    </>
  );

  return (
    <StrucRow className={"py-2"} {...ux2RowProps}>
      {/* Clarity masking (design 5.2/5.3): the order number is a non-personal
          id (unmask, Balanced would hide it); requisition number (the
          customer's free text), organisation and contact are masked, their
          tooltips too (portal = attribute on the trigger does not reach it).
          The GLS tracking number below stays masked on purpose. */}
      <StrucCol size={columns[nameOf<Order>((x) => x.orderNumber)].weight}>
        <Tooltip title={order.orderNumber} placement={"top-start"}>
          <span
            className={"whitespace-nowrap overflow-x-hidden overflow-ellipsis"}
            {...CLARITY_UNMASK}
          >
            {order.orderNumber}
          </span>
        </Tooltip>
      </StrucCol>
      <StrucCol
        size={columns[nameOf<Order>((x) => x.requisitionNumber)].weight}
      >
        <Tooltip
          title={order.requisitionNumber}
          placement={"top-start"}
          componentsProps={CLARITY_MASK_TOOLTIP}
        >
          <span
            className={"whitespace-nowrap overflow-x-hidden overflow-ellipsis"}
            {...CLARITY_MASK}
          >
            {order.requisitionNumber}
          </span>
        </Tooltip>
      </StrucCol>
      <StrucCol size={columns[nameOf<Order>((x) => x.organization)].weight}>
        <Tooltip
          title={order.organization.name}
          placement={"top-start"}
          componentsProps={CLARITY_MASK_TOOLTIP}
        >
          <span
            className={"whitespace-nowrap overflow-x-hidden overflow-ellipsis"}
            {...CLARITY_MASK}
          >
            {order.organization.name}
          </span>
        </Tooltip>
      </StrucCol>
      <StrucCol
        size={
          columns[nameOf<Order>((x) => x.orderDeliveryAddress?.contact)].weight
        }
        // Eneste FLEKSIBLE kolonne (weight 0 => flex:1). Flex-items må ikke
        // blive smallere end deres indhold uden min-w-0 - lange kontaktnavne
        // ("Viborg F.F Professionel Fodbold A/S") sprængte rækken, da T&T-
        // kolonnen indsnævrede restpladsen (rækken er flex-wrap => VIS-knappen
        // røg på egen linje). min-w-0 lader indholdet afkorte i stedet.
        className={"min-w-0 overflow-hidden"}
      >
        <Tooltip
          title={order.orderDeliveryAddress?.contact}
          placement={"top-start"}
          componentsProps={CLARITY_MASK_TOOLTIP}
        >
          <span
            className={"whitespace-nowrap overflow-x-hidden overflow-ellipsis"}
            {...CLARITY_MASK}
          >
            {order.orderDeliveryAddress?.contact}
          </span>
        </Tooltip>
      </StrucCol>
      <StrucCol size={columns[nameOf<Order>((x) => x.status)].weight}>
        <OrderStatusElement order={order} />
        {/* Admin price adjustment: "Adjusted vN" -> details with the history
            panel open. Self-gating (flag + admin + adjusted row), no call. */}
        <PriceAdjustedBadge order={order} />
      </StrucCol>
      <StrucCol size={columns[nameOf<Order>((x) => x.placedAtUtc)].weight}>
        {formatDate(order.placedAtUtc)}
      </StrucCol>
      <StrucCol size={columns[nameOf<Order>((x) => x.latestDelivery)].weight}>
        {order.latestDelivery ? formatDate(order.latestDelivery) : ""}
        {/* Requested delivery (flag): the customer's own pick as a second
            line - only when it differs from the expected date above. */}
        {requestedDelivery && (
          <span className={"text-xs text-gray-600 whitespace-nowrap"}>
            {t("orderListDetails.requestedDelivery", {
              date: formatDate(requestedDelivery),
            })}
          </span>
        )}
      </StrucCol>
      {/* Ret levering: T&T column - tracking link when the order has one,
          otherwise an em dash. stopPropagation so the row's own click
          handler never swallows the link. */}
      {enviroment.retLeveringEnabled && (
        <StrucCol size={columns[nameOf<Order>((x) => x.trackingNo)].weight}>
          {orderTrackingUrl(order) ? (
            <a
              href={orderTrackingUrl(order)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={
                "text-[#2E8A3A] font-semibold underline underline-offset-2 whitespace-nowrap overflow-x-hidden overflow-ellipsis"
              }
            >
              {order.trackingNo}
            </a>
          ) : (
            <span className={"text-gray-600"}>&mdash;</span>
          )}
        </StrucCol>
      )}
      <StrucCol
        size={columns["actions"].weight}
        className={"flex flex-col items-start !justify-around space-y-2 h-full"}
      >
        {actions}
      </StrucCol>
    </StrucRow>
  );
}
